import { Navigate, createBrowserRouter } from "react-router-dom";
import { useAuth } from "./auth/AuthProvider";
import { AppShell } from "./components/AppShell";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminPage } from "./pages/AdminPage";
import { AgentsPage } from "./pages/AgentsPage";
import { AuditPage } from "./pages/AuditPage";
import { BatchDetailPage } from "./pages/BatchDetailPage";
import { BatchesPage } from "./pages/BatchesPage";
import { CitizenPage } from "./pages/CitizenPage";
import { CyclesPage } from "./pages/CyclesPage";
import { DashboardPage } from "./pages/DashboardPage";
import { DeliveryPage } from "./pages/DeliveryPage";
import { ForbiddenPage, RouteErrorPage } from "./pages/ErrorPages";
import { HouseholdDetailPage } from "./pages/HouseholdDetailPage";
import { HouseholdsPage } from "./pages/HouseholdsPage";
import { ImportsPage } from "./pages/ImportsPage";
import { LoginPage } from "./pages/LoginPage";
import { ReportsPage } from "./pages/ReportsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { UnitsPage } from "./pages/UnitsPage";

function HomeRedirect() {
  const { session } = useAuth();
  if (!session) return <Navigate to="/login" replace />;
  if (session.subjectType === "citizen") return <Navigate to="/citizen" replace />;
  if (session.permissions.includes("distribution.deliver") && !session.permissions.includes("user.view")) {
    return <Navigate to="/delivery" replace />;
  }
  const options: Array<[string, string]> = [
    ["household.view", "/dashboard"],
    ["distribution.deliver", "/delivery"],
    ["distribution.view", "/batches"],
    ["agent.view", "/agents"],
    ["area.view", "/units"],
    ["report.view", "/reports"],
    ["audit.view", "/audit"],
    ["user.view", "/admin"]
  ];
  return (
    <Navigate
      to={options.find(([permission]) => session.permissions.includes(permission))?.[1] ?? "/forbidden"}
      replace
    />
  );
}
function staff(permission?: string) {
  return (
    <ProtectedRoute permission={permission}>
      <AppShell />
    </ProtectedRoute>
  );
}

export const router = createBrowserRouter([
  { path: "/", element: <HomeRedirect />, errorElement: <RouteErrorPage /> },
  { path: "/login", element: <LoginPage /> },
  {
    path: "/citizen",
    element: (
      <ProtectedRoute subject="citizen">
        <CitizenPage />
      </ProtectedRoute>
    )
  },
  {
    path: "/dashboard",
    element: staff("household.view"),
    children: [{ index: true, element: <DashboardPage /> }]
  },
  {
    path: "/units",
    element: staff("area.view"),
    children: [{ index: true, element: <UnitsPage /> }]
  },
  {
    path: "/households",
    element: staff("household.view"),
    children: [
      { index: true, element: <HouseholdsPage /> },
      { path: ":id", element: <HouseholdDetailPage /> }
    ]
  },
  {
    path: "/imports",
    element: staff("household.import"),
    children: [{ index: true, element: <ImportsPage /> }]
  },
  { path: "/agents", element: staff("agent.view"), children: [{ index: true, element: <AgentsPage /> }] },
  {
    path: "/cycles",
    element: staff("distribution.view"),
    children: [{ index: true, element: <CyclesPage /> }]
  },
  {
    path: "/batches",
    element: staff("distribution.view"),
    children: [
      { index: true, element: <BatchesPage /> },
      { path: ":id", element: <BatchDetailPage /> }
    ]
  },
  {
    path: "/delivery",
    element: staff("distribution.deliver"),
    children: [{ index: true, element: <DeliveryPage /> }]
  },
  { path: "/reports", element: staff("report.view"), children: [{ index: true, element: <ReportsPage /> }] },
  { path: "/audit", element: staff("audit.view"), children: [{ index: true, element: <AuditPage /> }] },
  { path: "/admin", element: staff("user.view"), children: [{ index: true, element: <AdminPage /> }] },
  {
    path: "/settings",
    element: staff("settings.manage"),
    children: [{ index: true, element: <SettingsPage /> }]
  },
  { path: "/forbidden", element: <ForbiddenPage /> },
  { path: "*", element: <RouteErrorPage /> }
]);
